import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, Btn, fmtDate, todayStr } from '../components/ui';
import LoadFailure from '../components/LoadFailure';
import { useConfirm } from '../components/ConfirmModal';

function friendlyMessageSheetsList(err) {
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('failed to fetch') || msg.includes('networkerror')) {
    return "You're offline or the server isn't reachable.";
  }
  return "Couldn't load sheets.";
}

function SwipeRow({ onDelete, children }) {
  const ref = useRef(null);
  const [startX, setStartX] = useState(null);
  const [offsetX, setOffsetX] = useState(0);
  const [swiped, setSwiped] = useState(false);

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (startX === null) return;
    const diff = e.touches[0].clientX - startX;
    // Only allow left swipe
    if (diff < 0) setOffsetX(Math.max(diff, -100));
    else setOffsetX(0);
  };

  const handleTouchEnd = () => {
    if (offsetX < -60) {
      setOffsetX(-80);
      setSwiped(true);
    } else {
      setOffsetX(0);
      setSwiped(false);
    }
    setStartX(null);
  };

  const close = () => { setOffsetX(0); setSwiped(false); };

  return (
    <div style={{ position:'relative', overflow:'hidden' }}>
      {/* Delete button behind */}
      <div style={{ position:'absolute', top:0, right:0, bottom:0, width:80, background:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <button onClick={() => { close(); onDelete(); }}
          style={{ background:'none', border:'none', cursor:'pointer', color:'#fff', fontFamily:'inherit', fontSize:12, fontWeight:700, display:'flex', flexDirection:'column', alignItems:'center', gap:2, padding:8 }}>
          <i className="fa-solid fa-trash" style={{ fontSize:16 }}></i>
          Delete
        </button>
      </div>
      {/* Swipeable content */}
      <div ref={ref}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => { if (swiped) close(); }}
        style={{ transform:`translateX(${offsetX}px)`, transition: startX !== null ? 'none' : 'transform 0.25s ease', position:'relative', zIndex:1 }}>
        {children}
      </div>
    </div>
  );
}

function AiCalendarPicker({ value, onChange }) {
  const today = todayStr();
  const safeDate = value || today;
  const [vy, setVy] = useState(new Date(safeDate+'T12:00:00').getFullYear());
  const [vm, setVm] = useState(new Date(safeDate+'T12:00:00').getMonth());

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const firstDay = new Date(vy, vm, 1).getDay();
  const daysInMonth = new Date(vy, vm+1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)];
  while(cells.length%7!==0) cells.push(null);

  const prevM = () => vm===0?(setVy(y=>y-1),setVm(11)):setVm(m=>m-1);
  const nextM = () => vm===11?(setVy(y=>y+1),setVm(0)):setVm(m=>m+1);
  const goToday = () => { const d = new Date(); setVy(d.getFullYear()); setVm(d.getMonth()); onChange(todayStr()); };

  useEffect(() => {
    if (value) { const d = new Date(value+'T12:00:00'); setVy(d.getFullYear()); setVm(d.getMonth()); }
  }, [value]);

  return (
    <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:16, padding:'16px 14px 12px' }}>
      {/* Month/Year nav */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, padding:'0 2px' }}>
        <button onClick={prevM} style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.04)', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:12, transition:'background 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(30,64,175,0.1)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <button onClick={goToday} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:'4px 8px', borderRadius:6 }}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          <span style={{ fontSize:15, fontWeight:800, color:'var(--text)', letterSpacing:-0.3 }}>{MONTHS[vm]}</span>
          <span style={{ fontSize:15, fontWeight:400, color:'var(--text2)', marginLeft:5 }}>{vy}</span>
        </button>
        <button onClick={nextM} style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.04)', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:12, transition:'background 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(30,64,175,0.1)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}>
          <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
        {['S','M','T','W','T','F','S'].map((d,i) => (
          <div key={i} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:'var(--text3)', padding:'4px 0', letterSpacing:0.5 }}>{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
        {cells.map((day,i) => {
          if (!day) return <div key={i} style={{ height:32 }} />;
          const ds = `${vy}-${String(vm+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isSel = ds === value;
          const isToday = ds === today;
          const isPast = ds < today;
          return (
            <button key={i} onClick={() => onChange(ds)}
              style={{
                width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center',
                borderRadius:'50%', border:'none', cursor:'pointer', fontFamily:'inherit',
                position:'relative', transition:'all 0.12s',
                background: isSel ? 'var(--blue)' : 'transparent',
                fontSize:13, fontWeight: isSel||isToday ? 700 : 400,
                color: isSel ? '#fff' : isPast ? 'var(--text3)' : 'var(--text)',
                boxShadow: isSel ? '0 2px 8px rgba(30,64,175,0.35)' : 'none',
              }}
              onMouseEnter={e => { if (!isSel) e.currentTarget.style.background='rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { if (!isSel) e.currentTarget.style.background='transparent'; }}>
              {day}
              {isToday && !isSel && <span style={{ position:'absolute', bottom:2, left:'50%', transform:'translateX(-50%)', width:4, height:4, borderRadius:'50%', background:'var(--blue)' }}></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function SheetsList() {
  const navigate = useNavigate();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [artists,    setArtists]    = useState([]);
  const [selArtist,  setSelArtist]  = useState('');
  const [sheets,     setSheets]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showPrev,   setShowPrev]   = useState(false);
  const [expanded,   setExpanded]   = useState({});
  const [search,     setSearch]     = useState('');
  const [aiModal,    setAiModal]    = useState(false);
  const [aiDate,     setAiDate]     = useState('');
  const [aiInstr,    setAiInstr]    = useState('');
  const [aiLoading,  setAiLoading]  = useState(false);
  const [aiError,    setAiError]    = useState('');
  const [aiMode,     setAiMode]     = useState('auto'); // 'auto' or 'ai'
  // Tracks the last failed sheets fetch so the empty-state can show a Try
  // again button instead of just hanging on a spinner.
  const [sheetsError, setSheetsError] = useState(null);

  useEffect(() => {
    api.getArtists().then(d => {
      setArtists(d.artists || []);
      if (d.artists?.length) setSelArtist(d.artists[0].id);
    });
  }, []);

  function loadSheets() {
    if (!selArtist) return Promise.resolve();
    setLoading(true);
    setSheetsError(null);
    return api.getSheets(selArtist)
      .then(d => setSheets(d.sheets || []))
      .catch(err => { setSheets([]); setSheetsError(friendlyMessageSheetsList(err)); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadSheets(); }, [selArtist]);

  const today = todayStr();

  const byDate = {};
  sheets.forEach(s => {
    if (!byDate[s.date]) byDate[s.date] = [];
    byDate[s.date].push(s);
  });
  Object.values(byDate).forEach(g => g.sort((a,b) => (b.status==='published'?1:0)-(a.status==='published'?1:0)));

  const futureDates = Object.keys(byDate).filter(d => d >= today).sort((a,b) => a.localeCompare(b));
  const pastDates   = Object.keys(byDate).filter(d => d < today).sort((a,b) => b.localeCompare(a));

  const SheetRow = ({ s }) => (
    <div onClick={() => navigate('/admin/sheets/'+s.id)}
      style={{ padding:'14px 20px', cursor:'pointer', background:'var(--bg2)', borderTop:'1px solid var(--border)',
        display:'flex', alignItems:'center', justifyContent:'space-between' }}
      onMouseEnter={e => e.currentTarget.style.background='var(--card)'}
      onMouseLeave={e => e.currentTarget.style.background='var(--bg2)'}>
      <div style={{ flex:1, minWidth:0, marginRight:12 }}>
        {(() => {
          const isJoint = s.artistIds?.length > 1;
          const isDraft = s.status !== 'published';
          let title;
          if (isJoint) {
            const names = (s.artistIds || []).map(id => artists.find(a => a.id === id)?.name).filter(Boolean).join(' & ');
            title = names || 'Joint Day Sheet';
          } else if (isDraft && !s.city) {
            title = 'Draft — ' + new Date(s.createdAt).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' });
          } else {
            title = s.city ? s.city.split(',').slice(0,2).join(',') : 'No location';
          }
          return <>
            <p style={{ fontSize:14, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {isJoint && <i className="fa-solid fa-users" style={{ fontSize:10, color:'var(--blue)', marginRight:6 }}></i>}
              {title}
            </p>
            {isJoint && s.city && <p style={{ fontSize:11, color:'var(--text3)', marginTop:1 }}>{s.city.split(',').slice(0,2).join(',')}</p>}
          </>;
        })()}
        <p style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>
          {s.events?.length ? `${s.events.length} event${s.events.length !== 1 ? 's' : ''}` : ''}
        </p>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
        {s.status === 'published'
          ? <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'rgba(63,185,80,0.15)', border:'1px solid rgba(63,185,80,0.4)', borderRadius:20, padding:'3px 8px', fontSize:11, fontWeight:700, color:'#3fb950' }}>● Live</span>
          : <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'rgba(210,153,34,0.1)', border:'1px solid rgba(210,153,34,0.3)', borderRadius:20, padding:'3px 8px', fontSize:11, fontWeight:700, color:'#d2991a' }}>● Draft</span>
        }
        <button onClick={e => {
          e.stopPropagation();
          const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          window.open(`${API}/email/pdf/${s.id}?token=${localStorage.getItem('ds_token')}`, '_blank');
        }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:13, padding:'4px 6px' }}
          title="Export PDF">
          <i className="fa-solid fa-file-pdf"></i>
        </button>
        <button onClick={async e => {
          e.stopPropagation();
          if (await confirmAction('Are you sure you want to delete this day sheet?', { title:'Delete Sheet', confirmLabel:'Delete', confirmColor:'var(--red)' })) {
            api.deleteSheet(s.id).then(() => setSheets(prev => prev.filter(x => x.id !== s.id))).catch(err => alert(err.message));
          }
        }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:13, padding:'4px 6px' }}
          title="Delete">
          <i className="fa-solid fa-xmark"></i>
        </button>
        <span style={{ fontSize:16, color:'var(--text3)' }}>›</span>
      </div>
    </div>
  );

  const toggle = (date) => setExpanded(prev => ({ ...prev, [date]: !prev[date] }));

  const DateCard = ({ date, isToday }) => {
    const isOpen = date in expanded ? expanded[date] : isToday;
    const group = byDate[date];
    const liveCount  = group.filter(s => s.status === 'published').length;
    const draftCount = group.filter(s => s.status !== 'published').length;
    const city = group.find(s => s.city)?.city;
    const dateStr = new Date(date+'T12:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric', year:'numeric' });

    return (
      <div style={{ marginBottom:8 }}>
        <div onClick={() => toggle(date)} style={{ background: isToday ? 'rgba(79,156,249,0.08)' : 'var(--card)',
          border: '1px solid '+(isToday ? 'var(--blue)' : 'var(--border)'),
          borderRadius: isOpen ? '12px 12px 0 0' : '12px', padding:'16px 20px', cursor:'pointer' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              {isToday && <p style={{ fontSize:10, fontWeight:800, letterSpacing:'1.5px', color:'var(--blue)', marginBottom:4 }}>TODAY</p>}
              <p style={{ fontSize:18, fontWeight:800, color: isToday ? 'var(--blue)' : 'var(--text)', marginBottom:2 }}>{dateStr}</p>
              {city && <p style={{ fontSize:12, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:500 }}><i className="fa-solid fa-location-dot" style={{ fontSize:12, marginRight:4 }}></i> {city.split(',').slice(0,2).join(',')}</p>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {liveCount > 0 && (
                <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(63,185,80,0.15)', border:'1px solid rgba(63,185,80,0.4)', borderRadius:20, padding:'4px 10px', fontSize:12, fontWeight:700, color:'#3fb950' }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:'#3fb950', display:'inline-block' }}/>{liveCount} Live
                </span>
              )}
              {draftCount > 0 && (
                <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(210,153,34,0.12)', border:'1px solid rgba(210,153,34,0.4)', borderRadius:20, padding:'4px 10px', fontSize:12, fontWeight:700, color:'#d2991a' }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:'#d2991a', display:'inline-block' }}/>{draftCount} Draft{draftCount>1?'s':''}
                </span>
              )}
              <span style={{ fontSize:16, color:'var(--text3)', display:'inline-block', transform: isOpen?'rotate(90deg)':'none', transition:'transform 0.2s' }}>›</span>
            </div>
          </div>
        </div>
        {isOpen && (
          <div style={{ border:'1px solid '+(isToday?'var(--blue)':'var(--border)'), borderTop:'none', borderRadius:'0 0 12px 12px', overflow:'hidden', marginBottom:4 }}>
            {group.map(s => (
              <SwipeRow key={s.id} onDelete={async () => {
                if (await confirmAction('Delete this day sheet?', { title:'Delete Sheet', confirmLabel:'Delete', confirmColor:'var(--red)' })) {
                  api.deleteSheet(s.id).then(() => setSheets(prev => prev.filter(x => x.id !== s.id))).catch(err => alert(err.message));
                }
              }}>
                <SheetRow s={s} />
              </SwipeRow>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: window.innerWidth < 768 ? '20px 16px' : '32px 40px', maxWidth:900, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:32, fontWeight:900, letterSpacing:-0.5, color:'var(--text)' }}>Sheets</h1>
        <p style={{ fontSize:14, color:'var(--text2)', marginTop:4 }}>Day sheets by date</p>
      </div>
      {/* Artist picker — circle avatars with names */}
      {artists.length > 1 && (
        <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap', justifyContent:'center' }}>
          {artists.map(a => {
            const isSel = selArtist===a.id;
            return (
              <button key={a.id} onClick={() => setSelArtist(a.id)}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:0, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: isSel ? 1 : 0.6, transition:'all 0.15s', minWidth:68 }}>
                <div style={{ width:60, height:60, borderRadius:'50%', border:`2.5px solid ${isSel ? 'var(--blue)' : 'var(--border)'}`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, overflow:'hidden',
                  background: a.photo ? 'none' : `${a.color||'var(--card)'}20`,
                  boxShadow: isSel ? '0 0 0 3px rgba(79,156,249,0.25)' : 'none', transition:'all 0.15s' }}>
                  {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji||a.name[0])}
                </div>
                <span style={{ fontSize:11, fontWeight:700, color: isSel ? 'var(--blue)' : 'var(--text2)', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <button onClick={() => navigate(`/admin/sheets/new?artist=${selArtist||''}`)}
          style={{ flex:1, padding:'16px', borderRadius:14, border:'none',
            background:'linear-gradient(135deg, #1E40AF, #6366f1)',
            boxShadow:'0 4px 15px rgba(30,64,175,0.3)',
            cursor:'pointer', fontFamily:'inherit', fontSize:15, fontWeight:800, color:'#fff',
            transition:'all 0.2s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(30,64,175,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 15px rgba(30,64,175,0.3)'; }}>
          <i className="fa-solid fa-plus" style={{ fontSize:13 }}></i>New Day Sheet
        </button>
        <button onClick={() => { setAiDate(''); setAiInstr(''); setAiError(''); setAiModal(true); }}
          style={{ flex:1, padding:'16px', borderRadius:14, border:'none',
            background:'linear-gradient(135deg, #8b5cf6, #d946ef)',
            boxShadow:'0 4px 15px rgba(139,92,246,0.3)',
            cursor:'pointer', fontFamily:'inherit', fontSize:15, fontWeight:800, color:'#fff',
            transition:'all 0.2s', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(139,92,246,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 15px rgba(139,92,246,0.3)'; }}>
          <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:13 }}></i>AI Generate Sheet
        </button>
      </div>

      {/* AI Generate Modal */}
      {aiModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => !aiLoading && setAiModal(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'var(--card)', borderRadius:20, padding:32, width:'100%', maxWidth:420,
              border:'1px solid var(--border)', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
              <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg, #8b5cf6, #d946ef)',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:16, color:'#fff' }}></i>
              </div>
              <div>
                <p style={{ fontSize:18, fontWeight:800, color:'var(--text)' }}>Generate Sheet</p>
                <p style={{ fontSize:12, color:'var(--text3)' }}>Pulls from Google Calendar, travel & event history</p>
              </div>
            </div>

            {/* Mode toggle */}
            <div style={{ display:'flex', gap:0, marginBottom:20, borderRadius:10, border:'1px solid var(--border)', overflow:'hidden' }}>
              <button onClick={() => setAiMode('auto')}
                style={{ flex:1, padding:'10px', border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
                  background: aiMode === 'auto' ? 'var(--blue)' : 'var(--bg2)',
                  color: aiMode === 'auto' ? '#fff' : 'var(--text3)',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 0.15s' }}>
                <i className="fa-solid fa-bolt" style={{ fontSize:11 }}></i>Auto
              </button>
              <button onClick={() => setAiMode('ai')}
                style={{ flex:1, padding:'10px', border:'none', borderLeft:'1px solid var(--border)', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
                  background: aiMode === 'ai' ? '#8b5cf6' : 'var(--bg2)',
                  color: aiMode === 'ai' ? '#fff' : 'var(--text3)',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 0.15s' }}>
                <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:11 }}></i>AI
              </button>
            </div>

            <label style={{ fontSize:12, fontWeight:700, color:'var(--text2)', display:'block', marginBottom:6 }}>Date</label>
            {aiDate && <p style={{ fontSize:14, fontWeight:600, color:'var(--text)', marginBottom:8 }}>{fmtDate(aiDate, {weekday:'long', month:'long', day:'numeric', year:'numeric'})}</p>}
            <div style={{ marginBottom:16 }}>
              <AiCalendarPicker value={aiDate} onChange={setAiDate} />
            </div>

            {aiMode === 'ai' && (<>
              <label style={{ fontSize:12, fontWeight:700, color:'var(--text2)', display:'block', marginBottom:6 }}>Instructions <span style={{ fontWeight:400, color:'var(--text3)' }}>(optional)</span></label>
              <textarea value={aiInstr} onChange={e => setAiInstr(e.target.value)}
                placeholder="e.g. Show is at The Ryman, doors at 7pm, soundcheck at 3pm..."
                rows={3}
                style={{ width:'100%', padding:'12px 14px', borderRadius:10, border:'1px solid var(--border)',
                  background:'var(--bg2)', color:'var(--text)', fontSize:14, fontFamily:'inherit', resize:'vertical', marginBottom:16, boxSizing:'border-box' }} />
            </>)}
            {aiMode === 'auto' && (
              <p style={{ fontSize:12, color:'var(--text3)', marginBottom:16, lineHeight:1.5 }}>
                <i className="fa-solid fa-bolt" style={{ fontSize:10, color:'var(--blue)', marginRight:4 }}></i>
                Instant — pulls calendar, travel, and event history directly. No AI processing.
              </p>
            )}

            {aiError && (
              <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)',
                color:'#ef4444', fontSize:13, marginBottom:16 }}>
                {aiError}
              </div>
            )}

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setAiModal(false)} disabled={aiLoading}
                style={{ flex:1, padding:'14px', borderRadius:12, border:'1px solid var(--border)', background:'var(--bg2)',
                  color:'var(--text2)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                Cancel
              </button>
              <button disabled={!aiDate || aiLoading}
                onClick={async () => {
                  setAiLoading(true);
                  setAiError('');
                  try {
                    const generateFn = aiMode === 'ai' ? api.agentGenerate : api.agentGenerateAuto;
                    const res = await generateFn({ artistId: selArtist, date: aiDate, instructions: aiMode === 'ai' ? (aiInstr || undefined) : undefined });
                    setAiModal(false);
                    if (res.sheet?.id) {
                      navigate('/admin/sheets/' + res.sheet.id + '/review');
                    } else if (res.success === false && !res.sheet) {
                      // Agent ran but didn't create a sheet — show the summary as error
                      setAiError(res.summary || 'Agent could not generate a sheet. Try again with more specific instructions.');
                      setAiModal(true);
                    } else {
                      // Fallback: try to find the sheet that was just created
                      const d = await api.getSheets(selArtist);
                      const newSheets = d.sheets || [];
                      const justCreated = newSheets.find(s => s.date === aiDate && s.status === 'draft');
                      if (justCreated) {
                        navigate('/admin/sheets/' + justCreated.id + '/review');
                      } else {
                        setSheets(newSheets);
                      }
                    }
                  } catch (err) {
                    setAiError(err.message || 'Failed to generate sheet');
                  } finally {
                    setAiLoading(false);
                  }
                }}
                style={{ flex:1, padding:'14px', borderRadius:12, border:'none',
                  background: !aiDate || aiLoading ? 'var(--border)' : 'linear-gradient(135deg, #8b5cf6, #d946ef)',
                  color: !aiDate || aiLoading ? 'var(--text3)' : '#fff',
                  fontSize:14, fontWeight:800, cursor: !aiDate || aiLoading ? 'not-allowed' : 'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {aiLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:13 }}></i>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:13 }}></i>
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search bar */}
      <Card style={{ padding:0, marginBottom:16, overflow:'hidden' }}>
        <div style={{ padding:'10px 14px', display:'flex', alignItems:'center', gap:10 }}>
          <i className="fa-solid fa-magnifying-glass" style={{ fontSize:13, color:'var(--text3)', flexShrink:0 }}></i>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search sheets..."
            style={{ flex:1, background:'transparent', border:'none', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', padding:0 }} />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:16, padding:0, lineHeight:1, flexShrink:0 }}>×</button>
          )}
        </div>
      </Card>

      {loading ? (
        <p style={{ color:'var(--text2)', padding:40 }}>Loading...</p>
      ) : sheetsError ? (
        <LoadFailure
          title="Couldn't load sheets"
          message={sheetsError}
          onRetry={loadSheets}
        />
      ) : sheets.length === 0 ? (
        <Card style={{ padding:40, textAlign:'center' }}>
          <i className="fa-regular fa-clipboard" style={{ fontSize:40, color:'var(--text3)', display:'block', marginBottom:12 }}></i>
          <p style={{ fontSize:16, color:'var(--text)', marginBottom:16 }}>No day sheets yet</p>
          <Btn variant="primary" onClick={() => navigate(`/admin/sheets/new?artist=${selArtist||''}`)}>Create First Sheet</Btn>
        </Card>
      ) : (() => {
        // Filter by search
        const q = search.toLowerCase();
        const searchSheets = q ? sheets.filter(s =>
          (s.city||'').toLowerCase().includes(q) || (s.date||'').includes(q) ||
          (s.notes||'').toLowerCase().includes(q) || (s.status||'').toLowerCase().includes(q)
        ) : sheets;

        // Regroup filtered sheets by date
        const sByDate = {};
        searchSheets.forEach(s => {
          if (!sByDate[s.date]) sByDate[s.date] = [];
          sByDate[s.date].push(s);
        });
        Object.values(sByDate).forEach(g => g.sort((a,b) => (b.status==='published'?1:0)-(a.status==='published'?1:0)));
        const fDates = Object.keys(sByDate).filter(d => d >= today).sort((a,b) => a.localeCompare(b));
        const pDates = Object.keys(sByDate).filter(d => d < today).sort((a,b) => b.localeCompare(a));

        if (q && searchSheets.length === 0) return <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>No matches</p>;

        return (
        <>
          {/* Upcoming + today */}
          {fDates.length === 0 && !q ? (
            <Card style={{ padding:24, textAlign:'center', marginBottom:16 }}>
              <p style={{ color:'var(--text3)', fontSize:14 }}>No upcoming sheets — create one!</p>
            </Card>
          ) : (
            fDates.map(date => (
              <DateCard key={date} date={date} isToday={date===today} />
            ))
          )}

          {/* Previous sheets */}
          {pDates.length > 0 && (
            <div style={{ marginTop:8 }}>
              {q ? (
                // When searching, show all matching past dates expanded
                <div>
                  {pDates.map(date => (
                    <DateCard key={date} date={date} isToday={false} />
                  ))}
                </div>
              ) : (
                <>
                  <button onClick={() => setShowPrev(v => !v)}
                    style={{ width:'100%', padding:'14px 20px', borderRadius:12, border:'1px solid var(--border)', background:'var(--card)',
                      cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700, color:'var(--text2)',
                      display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span><i className="fa-solid fa-folder" style={{ fontSize:14, marginRight:4 }}></i> Previous Sheets ({pDates.length} day{pDates.length!==1?'s':''})</span>
                    <span style={{ fontSize:16, transform: showPrev?'rotate(90deg)':'none', transition:'transform 0.2s', display:'inline-block' }}>›</span>
                  </button>
                  {showPrev && (
                    <div style={{ marginTop:8 }}>
                      {pDates.map(date => (
                        <DateCard key={date} date={date} isToday={false} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
        );
      })()}
      {ConfirmDialog}
    </div>
  );
}
